import { type UsFilters, RoleSelect, UsStateSelect, UsTableFilters } from 'components'
import { FormControl, Grid, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import React from 'react'
import { type Role } from 'services'
import { useSessionContext, type NameMatchMode, NAME_MATCH_MODE_DEFAULT } from 'session'
import { BranchSelect } from 'components/us/common/branch-select'
import { US_GENERAL_BRANCHES, US_COMMITTEE_ROLES, US_ADDITIONAL_ENTITY_ROLES, US_ELECT_ROLES, US_CANDIDATE_ROLES } from 'utils'

interface PeopleFilterProps {
  filters: UsFilters
  updateFilters: (filters: any) => void
  doFilter: () => Promise<void>
  cleanFilters: () => void
}

export const UsPeopleFilters: React.FC<PeopleFilterProps> = ({ filters, updateFilters, doFilter, cleanFilters }) => {
  const { sessionData, changePeopleNameMatchMode } = useSessionContext()
  const executiveBranchesIds = [US_GENERAL_BRANCHES.FEDERAL_EXECUTIVE_BRANCH, US_GENERAL_BRANCHES.STATE_EXECUTIVE_BRANCH]

  const entityRoleIds = [
    ...Object.values(US_COMMITTEE_ROLES),
    ...Object.values(US_ADDITIONAL_ENTITY_ROLES),
    ...Object.values(US_ELECT_ROLES),
    ...Object.values(US_CANDIDATE_ROLES)
  ]

  const getPositions = (roles: Role[]): Role[] => {
    if (filters.branch !== null && filters.branch.id === 'executive') {
      return roles.filter((role: Role) => executiveBranchesIds.includes(role.branch.id))
    }

    const personRoles = roles.filter((role: Role) => !entityRoleIds.includes(role.id))

    if (filters.branch !== null && filters.branch.id === 'legislative') {
      return personRoles.filter((role: Role) =>
        !executiveBranchesIds.includes(role.branch.id)
      )
    }
    return personRoles
  }

  const handleBranchChange = (branch: { id: string, desc: string } | null): void => {
    updateFilters({ branch, role: null })
  }

  return (
    <UsTableFilters doFilter={doFilter} filters={filters} updateFilters={updateFilters} cleanFilters={cleanFilters}>
      <Grid item xs={4}>
        <FormControl variant="outlined" fullWidth={true}>
          <TextField
            label="First Name"
            value={filters.firstName ?? ''}
            onChange={(event) => { updateFilters({ firstName: event.target.value }) }}
            variant="outlined"
            fullWidth
          />
        </FormControl>
      </Grid>
      <Grid item xs={4}>
        <FormControl variant="outlined" fullWidth={true}>
          <TextField
            label="Last Name"
            value={filters.lastName ?? ''}
            onChange={(event) => { updateFilters({ lastName: event.target.value }) }}
            variant="outlined"
            fullWidth
          />
        </FormControl>
      </Grid>
      <Grid item xs={2}>
        <FormControl variant="outlined" fullWidth={true}>
          <TextField
            label="ID"
            value={filters.id ?? ''}
            onChange={(event) => { updateFilters({ id: event.target.value }) }}
            variant="outlined"
            fullWidth
          />
        </FormControl>
      </Grid>
      <Grid item xs={2}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Name match</Typography>
        <ToggleButtonGroup
          exclusive
          fullWidth
          size="small"
          color="primary"
          value={sessionData.peopleNameMatchMode ?? NAME_MATCH_MODE_DEFAULT}
          onChange={(_event, next: NameMatchMode | null) => { if (next != null) changePeopleNameMatchMode(next) }}
        >
          <ToggleButton value="contains">Contains</ToggleButton>
          <ToggleButton value="starts_with">Starts with</ToggleButton>
        </ToggleButtonGroup>
      </Grid>
      <Grid item xs={3}>
        <FormControl variant="outlined" fullWidth={true}>
          <BranchSelect
            updateBranch={handleBranchChange}
            value={filters.branch}
            disabled={false}
          />
        </FormControl>
      </Grid>
      <Grid item xs={3}>
        <FormControl variant="outlined" fullWidth={true}>
          <RoleSelect
            updateRole={(role) => { updateFilters({ role }) }}
            getRoleFilter={getPositions}
            value={filters.role}
            disabled={false}
            updateBranch={(branch) => { updateFilters({ branch }) }}
          />
        </FormControl>
      </Grid>
      <Grid item xs={3}>
        <FormControl variant="outlined" fullWidth={true}>
          <UsStateSelect value={filters.state} updateState={(state) => { updateFilters({ state }) }} onlyUSStates={true} disabled={false}/>
        </FormControl>
      </Grid>
      <Grid item xs={3}>
        <FormControl variant="outlined" fullWidth={true}>
          <TextField
            label="Email"
            value={filters.email ?? ''}
            onChange={(event) => { updateFilters({ email: event.target.value }) }}
            variant="outlined"
            fullWidth
          />
        </FormControl>
      </Grid>
    </UsTableFilters>
  )
}
